import { Button } from "@package/subframe/components/Button";
import { TextField } from "@package/subframe/components/TextField";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, Navigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@/contexts/AuthContext";
import {
  useGetPlatformSettingsQueryControllerGet,
  useUpdatePlatformSettingsCommandControllerUpdate,
} from "@/generated/platform-settings/platform-settings";

export const Route = createFileRoute(
  "/_authenticated/platform-settings/streak-rules",
)({
  component: PlatformStreakRulesPage,
});

/** Matches the bounds the API enforces on streakGraceHours. */
const MIN_GRACE_HOURS = 0;
const MAX_GRACE_HOURS = 48;

function PlatformStreakRulesPage() {
  const { userRoleType, isLoading: authLoading } = useAuth();
  const isPlatformAdmin = userRoleType === "PLATFORM_ADMIN";

  const queryClient = useQueryClient();

  // Gate the fetch on admin so a non-admin never issues the request before the
  // redirect below kicks in.
  const { data: settingsResponse, isLoading } =
    useGetPlatformSettingsQueryControllerGet({
      query: { enabled: isPlatformAdmin },
    });
  const settings = settingsResponse?.data;

  const updateMutation = useUpdatePlatformSettingsCommandControllerUpdate({
    mutation: {
      onSuccess: () =>
        // The dashboard streak reports the window it enforced, so it has to be
        // refetched alongside the settings themselves.
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === "/v1/platform-settings" ||
            String(query.queryKey[0]).endsWith("/dashboard/streak"),
        }),
    },
  });

  const [graceRaw, setGraceRaw] = useState("");

  // Seed the input from the saved value once the settings load.
  useEffect(() => {
    if (settings?.streakGraceHours != null) {
      setGraceRaw(String(settings.streakGraceHours));
    }
  }, [settings?.streakGraceHours]);

  // Wait for auth to resolve before deciding — avoids flashing admin UI to a
  // user who is about to be redirected.
  if (authLoading) {
    return (
      <div className="flex w-full items-center justify-center py-12">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    );
  }
  if (!isPlatformAdmin) {
    return <Navigate to="/dashboard" />;
  }

  if (isLoading) {
    return (
      <div className="flex w-full items-center justify-center py-12">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    );
  }

  const parsed = graceRaw.trim() === "" ? null : Number(graceRaw);
  const isInvalid =
    parsed !== null &&
    (!Number.isInteger(parsed) ||
      parsed < MIN_GRACE_HOURS ||
      parsed > MAX_GRACE_HOURS);

  const handleSave = async () => {
    if (parsed === null || isInvalid) {
      toast.error(
        `Enter a whole number of hours between ${MIN_GRACE_HOURS} and ${MAX_GRACE_HOURS}.`,
      );
      return;
    }
    try {
      await updateMutation.mutateAsync({ data: { streakGraceHours: parsed } });
      toast.success("Streak rules saved.");
    } catch {
      toast.error("Failed to save streak rules.");
    }
  };

  return (
    <div className="flex w-full max-w-md flex-col gap-6 animate-fade-in">
      <div className="flex flex-col items-start gap-1">
        <Link
          to="/platform-settings"
          className="text-caption font-caption text-subtext-color hover:text-default-font"
        >
          ← Platform settings
        </Link>
        <span className="w-full text-metrics font-metrics text-primary-navy-800">
          Streak rules
        </span>
        <span className="text-body font-body text-subtext-color">
          How long after its due time a checklist can still be completed and
          keep the nursery&apos;s streak. Measured from the checklist&apos;s own
          due time, so one due at midday still counts until 6am the next
          morning. Checklists with a hard deadline get no grace at all.
        </span>
      </div>

      <TextField
        label="Grace period (hours)"
        helpText={
          settings?.isStreakGraceDefault
            ? `Currently using the default of ${settings.streakGraceHours} hours. Set 0 to require completion strictly on time.`
            : `Set 0 to require completion strictly on time. Maximum ${MAX_GRACE_HOURS} hours.`
        }
        error={isInvalid}
      >
        <TextField.Input
          type="number"
          step="1"
          inputMode="numeric"
          placeholder={String(settings?.streakGraceHours ?? "")}
          value={graceRaw}
          onChange={(e) => setGraceRaw(e.target.value)}
        />
      </TextField>

      <Button
        variant="brand-primary"
        size="medium"
        loading={updateMutation.isPending}
        disabled={updateMutation.isPending || isInvalid || parsed === null}
        onClick={handleSave}
      >
        Save streak rules
      </Button>
    </div>
  );
}
